import axios from "axios";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { HEADERS_EXPORT_BEN, POST_LOAN_DB2 } from "../../API/apiUrls";

// ขั้นตอนการทำงาน (ตาม seize_status) ลำดับเดียวกับกราฟ
export const SEIZE_GROUPS = [
  { key: "notSeize", label: "ยังไม่ยึด", color: "#8c8c8c" },
  { key: "setupSeize", label: "ตั้งเรื่องยึด", color: "#fa8c16" },
  { key: "seizeSuccess", label: "ยึดสำเร็จ", color: "#52c41a" },
];

// 0,3 = ยึดสำเร็จ | 1,4 = ตั้งเรื่องยึด | อื่น ๆ (รวม null) = ยังไม่ยึด
export const getSeizeGroupKey = (seizeStatus) =>
  seizeStatus === 0 || seizeStatus === 3
    ? "seizeSuccess"
    : seizeStatus === 1 || seizeStatus === 4
      ? "setupSeize"
      : "notSeize";

export const getSeizeStatusText = (seizeStatus) =>
  SEIZE_GROUPS.find((g) => g.key === getSeizeGroupKey(seizeStatus)).label;

// "#fa8c16" -> "FFFA8C16" (สีของ exceljs)
export const toArgb = (hex) => `FF${hex.replace("#", "").toUpperCase()}`;

// ประเภทเอกสารสิทธิ์ (property_type_id)
export const propertyTypeLabel = (id) =>
  id ? (id === 1 ? "น.ส.4 จ" : "น.ส.3 ก") : "-";

// ลักษณะทรัพย์ (property_detail_id)
export const PROPERTY_DETAILS = {
  1: "ที่ดินว่างเปล่า",
  2: "ที่ตาบอด",
  3: "ที่ดินพร้อมสิ่งปลูกสร้าง",
  4: "ที่นา",
  5: "ห้องชุด",
  6: "บ้านเอื้ออาทร",
  7: "ที่ดินต้องห้าม",
  8: "จดรวม 2 โฉนด",
  9: "ที่ถนนทางเข้า",
  10: "ไม่ได้ลงพื้นที่ตรวจสอบ",
};

export const propertyDetailLabel = (id) => PROPERTY_DETAILS[id] || "-";

// เนื้อที่ ไร่-งาน-ตร.วา
export const formatArea = (record) =>
  [record.rai, record.ngan, record.wa].every(
    (v) => v === null || v === undefined,
  )
    ? "-"
    : `${record.rai || 0}-${record.ngan || 0}-${record.wa || 0}`;

// ติดอายัด (sequestrate_status === 1)
export const isSequestrated = (record) =>
  Number(record.sequestrate_status) === 1;

// ติดจำนอง (มีประเภทจำนอง หรือ ผู้รับจำนอง)
export const isMortgaged = (record) =>
  !!(record.mortgage_type || record.mortgagee);

// สถานะทรัพย์ (นับซ้ำกับขั้นตอนการทำงานได้)
export const ASSET_FLAG_GROUPS = [
  {
    key: "sequestrated",
    label: "ติดอายัด",
    color: "#cf1322",
    test: isSequestrated,
  },
  { key: "mortgaged", label: "ติดจำนอง", color: "#722ed1", test: isMortgaged },
];

export const getLocation = (record) =>
  [record.dist_desc, record.prov_desc].filter(Boolean).join(" ");

export const getMortgageText = (record) =>
  record.mortgage_type || record.mortgagee
    ? `${record.mortgage_type || "-"}${
        record.mortgagee ? ` : ${record.mortgagee}` : ""
      }`
    : "";

// const DEBT_NCSHPRC_TYPES = ["lsfhp-h-ksm", "lsfhp-ksm", "rpsl", "rpsl-h"];
const DEBT_NCSHPRC_TYPES = ["rpsl", "rpsl-h"];

export const getDebt = (db2) => {
  if (!db2) return null;
  return DEBT_NCSHPRC_TYPES.includes(db2.LOAN?.DATA_TYPE)
    ? db2.LOAN?.NCSHPRC
    : db2.LOAN?.TOTPRC;
};

// ยอดหนี้คงเหลือ = ยอดหนี้ - จ่ายมาทั้งหมด (SMPAY)
export const getRemainDebt = (db2) => {
  const debt = getDebt(db2);
  if (debt === null || debt === undefined || debt === "") return null;
  const remain = Number(debt) - Number(db2.LOAN?.SMPAY || 0);
  return isNaN(remain) ? null : Math.round(remain * 100) / 100;
};

export const getCustomerName = (item) =>
  `${item.SNAM || ""}${item.NAME1 || ""} ${item.NAME2 || ""}`.trim();

// เรียงตาม ผู้รับผิดชอบ -> วันที่ยึด -> วันที่สืบทรัพย์ (ค่าว่างไว้ท้าย)
const compareDate = (a, b) => {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return new Date(a) - new Date(b);
};

export const sortCases = (rows) =>
  [...rows].sort(
    (a, b) =>
      (a.LAWYER_NNAME || "").localeCompare(b.LAWYER_NNAME || "", "th") ||
      compareDate(a.seize_date, b.seize_date) ||
      compareDate(a.investigation_date, b.investigation_date),
  );

// เพิ่มข้อมูลสัญญา (db2) ที่ตาราง / excel ใช้ร่วมกัน
export const enrichCase = (item, loanByContno = {}) => ({
  ...item,
  db2: loanByContno[item.CONTNO?.trim()],
});

// โหลดข้อมูลสัญญาจาก POST_LOAN_DB2 -> { CONTNO: item }
export const loadLoanByContno = async (items) => {
  const contnos = [...new Set(items.map((i) => i.CONTNO).filter(Boolean))];
  if (contnos.length === 0) return {};
  const response = await axios.post(
    POST_LOAN_DB2,
    { CONTNO: contnos.map((val) => `'${val}'`).join(",") },
    { headers: HEADERS_EXPORT_BEN },
  );
  const mapped = {};
  (Array.isArray(response.data) ? response.data : []).forEach((item) => {
    const contno = item?.LOAN?.CONTNO;
    if (contno) mapped[contno.trim()] = item;
  });
  return mapped;
};

// ---------- Excel ----------

const toNumber = (value) =>
  value === null || value === undefined || value === "" || isNaN(Number(value))
    ? null
    : Number(value);

const MONEY_FMT = "#,##0.00";

// คอลัมน์ excel ให้ตรงกับตารางใน DataCheck
const buildColumns = (formatDate) => {
  const date = (v) => (v ? formatDate(v) : "-");
  return [
    { header: "ลำดับ", width: 7, value: (r, i) => i + 1 },
    { header: "เลขสัญญา", width: 14, value: (r) => r.CONTNO || "-" },
    {
      header: "ชื่อลูกค้า",
      width: 30,
      value: (r) => getCustomerName(r) || "-",
    },
    { header: "ผู้รับผิดชอบ", width: 16, value: (r) => r.LAWYER_NNAME || "-" },
    { header: "สาขา", width: 8, value: (r) => r.LOCAT || "-" },
    {
      header: "ขั้นตอนการทำงาน",
      width: 16,
      value: (r) => getSeizeStatusText(r.seize_status),
    },
    {
      group: "คดี",
      header: "คดีแดง",
      width: 16,
      value: (r) => r.red_case_number || "-",
    },
    {
      group: "คดี",
      header: "วันพิพากษา",
      width: 13,
      value: (r) => date(r.judge_date),
    },
    {
      group: "สืบทรัพย์",
      header: "วันที่สืบ",
      width: 13,
      value: (r) => date(r.investigation_date),
    },
    {
      group: "สืบทรัพย์",
      header: "ผู้ถือครอง",
      width: 26,
      wrap: true,
      value: (r) => r.possessor?.trim() || "-",
    },
    {
      group: "สืบทรัพย์",
      header: "ประเภทเอกสารสิทธิ์",
      width: 14,
      value: (r) => propertyTypeLabel(r.property_type_id),
    },
    {
      group: "สืบทรัพย์",
      header: "ลักษณะทรัพย์",
      width: 22,
      value: (r) => propertyDetailLabel(r.property_detail_id),
    },
    {
      group: "สืบทรัพย์",
      header: "เลขโฉนด",
      width: 12,
      value: (r) => r.deed_number || "-",
    },
    {
      group: "สืบทรัพย์",
      header: "เนื้อที่ (ไร่-งาน-วา)",
      width: 14,
      value: (r) => formatArea(r),
    },
    {
      group: "สืบทรัพย์",
      header: "ที่ตั้ง",
      width: 28,
      wrap: true,
      value: (r) => getLocation(r) || "-",
    },
    {
      group: "สืบทรัพย์",
      header: "ติดจำนอง",
      width: 11,
      value: (r) => (isMortgaged(r) ? "ติดจำนอง" : "ไม่ติดจำนอง"),
    },
    {
      group: "สืบทรัพย์",
      header: "ภาระจำนอง",
      width: 32,
      wrap: true,
      value: (r) => getMortgageText(r) || "-",
    },
    {
      group: "สืบทรัพย์",
      header: "วันที่จำนอง",
      width: 22,
      value: (r) =>
        r.mortgage_start_date || r.mortgage_end_date
          ? `${date(r.mortgage_start_date)} - ${date(r.mortgage_end_date)}`
          : "-",
    },
    {
      group: "สืบทรัพย์",
      header: "ยอดจำนอง",
      width: 15,
      money: true,
      value: (r) => toNumber(r.mortgage_balance),
    },
    {
      group: "ยึดทรัพย์",
      header: "ติดอายัด",
      width: 26,
      wrap: true,
      value: (r) =>
        isSequestrated(r)
          ? `ติดอายัด${r.preference_creditor ? ` กับ ${r.preference_creditor}` : ""}`
          : "ไม่ติดอายัด",
    },
    {
      group: "ยึดทรัพย์",
      header: "วันที่ยึด",
      width: 13,
      value: (r) => date(r.seize_date),
    },
    {
      group: "ยึดทรัพย์",
      header: "สำนักงานบังคับคดี",
      width: 20,
      value: (r) => r.legal_execution_office || "-",
    },
    {
      group: "ยึดทรัพย์",
      header: "ราคาประเมิน (บังคับคดี)",
      width: 18,
      money: true,
      value: (r) => toNumber(r.estimated_enforce_price),
    },
    {
      group: "ข้อมูลสัญญา",
      header: "ยอดหนี้",
      width: 15,
      money: true,
      value: (r) => toNumber(getDebt(r.db2)),
    },
    {
      group: "ข้อมูลสัญญา",
      header: "จ่ายมาทั้งหมด",
      width: 15,
      money: true,
      value: (r) => (r.db2 ? toNumber(r.db2.LOAN?.SMPAY) : null),
    },
    {
      group: "ข้อมูลสัญญา",
      header: "ยอดหนี้คงเหลือ",
      width: 15,
      money: true,
      value: (r) => getRemainDebt(r.db2),
    },
    {
      group: "ข้อมูลสัญญา",
      header: "จ่ายล่าสุด",
      width: 13,
      value: (r) => date(r.db2?.LOAN?.LPAYD),
    },
    {
      header: "หมายเหตุ",
      width: 35,
      wrap: true,
      value: (r) =>
        [r.mark, r.seize_status_mark].filter(Boolean).join("\n") || "-",
    },
  ];
};

const thinBorder = {
  top: { style: "thin" },
  left: { style: "thin" },
  bottom: { style: "thin" },
  right: { style: "thin" },
};

const fill = (argb) => ({
  type: "pattern",
  pattern: "solid",
  fgColor: { argb },
});

// ชื่อ sheet ห้ามมี \ / ? * [ ] : และยาวไม่เกิน 31 ตัว
const safeSheetName = (name) => name.replace(/[\\/?*[\]:]/g, "-").slice(0, 31);

const addCaseSheet = (workbook, sheet, columns) => {
  const { name, color, rows, subtitle } = sheet;
  const headerOf = (col) =>
    typeof col.header === "function" ? col.header(sheet) : col.header;
  const ws = workbook.addWorksheet(safeSheetName(name), {
    views: [{ state: "frozen", xSplit: 2, ySplit: 4 }],
  });
  const lastCol = columns.length;
  ws.columns = columns.map((c) => ({ width: c.width }));

  // หัวรายงาน
  ws.mergeCells(1, 1, 1, lastCol);
  const title = ws.getCell(1, 1);
  title.value = `${name} (${rows.length} รายการ)`;
  title.font = { bold: true, size: 14, color: { argb: "FFFFFFFF" } };
  title.fill = fill(color);
  title.alignment = { vertical: "middle", horizontal: "left" };
  ws.getRow(1).height = 24;

  ws.mergeCells(2, 1, 2, lastCol);
  ws.getCell(2, 1).value = subtitle || "";
  ws.getCell(2, 1).font = { italic: true, color: { argb: "FF595959" } };

  // หัวตาราง 2 แถว (กลุ่ม / คอลัมน์) เหมือนตารางในหน้าจอ
  const groupRow = 3;
  const headRow = 4;
  let i = 0;
  while (i < columns.length) {
    const col = columns[i];
    if (!col.group) {
      ws.mergeCells(groupRow, i + 1, headRow, i + 1);
      ws.getCell(groupRow, i + 1).value = headerOf(col);
      i += 1;
      continue;
    }
    let j = i;
    while (j + 1 < columns.length && columns[j + 1].group === col.group) j += 1;
    ws.mergeCells(groupRow, i + 1, groupRow, j + 1);
    ws.getCell(groupRow, i + 1).value = col.group;
    for (let k = i; k <= j; k += 1) {
      ws.getCell(headRow, k + 1).value = headerOf(columns[k]);
    }
    i = j + 1;
  }
  [groupRow, headRow].forEach((r) => {
    for (let c = 1; c <= lastCol; c += 1) {
      const cell = ws.getCell(r, c);
      cell.font = { bold: true };
      cell.fill = fill("FFE8EDF5");
      cell.border = thinBorder;
      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
        wrapText: true,
      };
    }
  });

  // ข้อมูล
  rows.forEach((record, index) => {
    const row = ws.addRow(columns.map((c) => c.value(record, index) ?? "-"));
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      const col = columns[colNumber - 1];
      cell.border = thinBorder;
      const isNumber = typeof cell.value === "number";
      if (col.money && isNumber) cell.numFmt = MONEY_FMT;
      cell.alignment = {
        vertical: "top",
        horizontal: isNumber ? "right" : col.wrap ? "left" : undefined,
        wrapText: true,
      };
      if (index % 2 === 1) cell.fill = fill("FFFAFAFA");
    });
  });

  // แถวรวม (เฉพาะคอลัมน์เงิน)
  if (rows.length > 0) {
    const firstData = headRow + 1;
    const lastData = headRow + rows.length;
    const total = ws.addRow([]);
    total.getCell(1).value = "รวม";
    ws.mergeCells(total.number, 1, total.number, 3);
    columns.forEach((c, idx) => {
      if (!c.money) return;
      const letter = ws.getColumn(idx + 1).letter;
      total.getCell(idx + 1).value = {
        formula: `SUM(${letter}${firstData}:${letter}${lastData})`,
      };
      total.getCell(idx + 1).numFmt = MONEY_FMT;
    });
    total.eachCell({ includeEmpty: true }, (cell) => {
      cell.font = { bold: true };
      cell.fill = fill("FFFFF7E6");
      cell.border = thinBorder;
    });
  }
};

const addSummarySheet = (workbook, sheets, subtitle) => {
  const ws = workbook.addWorksheet("สรุป");
  ws.columns = [
    { width: 24 },
    { width: 14 },
    { width: 18 },
    { width: 22 },
    { width: 18 },
    { width: 18 },
  ];
  ws.mergeCells("A1:F1");
  ws.getCell("A1").value = "สรุปตามขั้นตอนการทำงาน";
  ws.getCell("A1").font = { bold: true, size: 14 };
  ws.mergeCells("A2:F2");
  ws.getCell("A2").value = subtitle || "";
  ws.getCell("A2").font = { italic: true, color: { argb: "FF595959" } };

  const header = ws.addRow([
    "ขั้นตอนการทำงาน",
    "จำนวนรายการ",
    "ยอดจำนอง",
    "ราคาประเมิน (บังคับคดี)",
    "ยอดหนี้",
    "ยอดหนี้คงเหลือ",
  ]);
  header.eachCell((cell) => {
    cell.font = { bold: true };
    cell.fill = fill("FFE8EDF5");
    cell.border = thinBorder;
    cell.alignment = { horizontal: "center" };
  });

  const sum = (rows, fn) =>
    rows.reduce((s, r) => s + (toNumber(fn(r)) || 0), 0);
  const addSheetRow = (sheet) => {
    const row = ws.addRow([
      sheet.name,
      sheet.rows.length,
      sum(sheet.rows, (r) => r.mortgage_balance),
      sum(sheet.rows, (r) => r.estimated_enforce_price),
      sum(sheet.rows, (r) => getDebt(r.db2)),
      sum(sheet.rows, (r) => getRemainDebt(r.db2)),
    ]);
    row.getCell(1).fill = fill(sheet.color);
    row.getCell(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
    row.eachCell({ includeEmpty: true }, (cell, col) => {
      cell.border = thinBorder;
      if (col >= 3) cell.numFmt = MONEY_FMT;
    });
  };
  // sheet ที่มี extra (เช่น ติดอายัด / ติดจำนอง) นับซ้ำกับขั้นตอนการทำงาน ไม่รวมในแถวรวม
  const mainSheets = sheets.filter((sheet) => !sheet.extra);
  const extraSheets = sheets.filter((sheet) => sheet.extra);
  const firstData = header.number + 1;
  mainSheets.forEach(addSheetRow);
  const lastData = ws.lastRow.number;
  const total = ws.addRow(["รวม"]);
  ["B", "C", "D", "E", "F"].forEach((letter) => {
    total.getCell(letter).value = {
      formula: `SUM(${letter}${firstData}:${letter}${lastData})`,
    };
  });
  total.font = { bold: true };

  for (let r = firstData; r <= total.number; r += 1) {
    ws.getRow(r).eachCell({ includeEmpty: true }, (cell, col) => {
      cell.border = thinBorder;
      if (col >= 3) cell.numFmt = MONEY_FMT;
    });
  }
  total.eachCell((cell) => (cell.fill = fill("FFFFF7E6")));

  if (extraSheets.length > 0) {
    ws.addRow([]);
    const extraHeader = ws.addRow(["สถานะทรัพย์ (นับซ้ำกับขั้นตอนการทำงาน)"]);
    extraHeader.font = { bold: true };
    extraSheets.forEach(addSheetRow);
  }
};

/**
 * export excel ตามตาราง แยก sheet ตามงาน
 * sheets: [{ name, color, rows: enrichCase[] }]
 */
export const exportCasesExcel = async ({
  sheets,
  fileName,
  subtitle,
  formatDate,
  withSummary = true,
}) => {
  const workbook = new ExcelJS.Workbook();
  const columns = buildColumns(formatDate);
  // รับได้ทั้ง name หรือ label (STATUS_GROUPS)
  sheets = sheets.map((sheet) => ({
    ...sheet,
    name: sheet.name || sheet.label,
    rows: sortCases(sheet.rows),
  }));

  if (withSummary && sheets.length > 1) {
    addSummarySheet(workbook, sheets, subtitle);
  }
  sheets
    .filter((sheet) => sheet.rows.length > 0 || sheets.length === 1)
    .forEach((sheet) =>
      addCaseSheet(workbook, { ...sheet, subtitle }, columns),
    );

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    fileName,
  );
};
